import React, { useEffect, useMemo, useState } from 'react';
import RuntimeConfigGridToolbar from './RuntimeConfigGridToolbar';
import RuntimeConfigGridPagination from './RuntimeConfigGridPagination';
import { BROWSE_PAGE_SIZE } from './runtimeConfigUtils';
import {
  buildLiveUrl,
  buildProxyEditPrefill,
  formatRerouteDisplay,
  proxyRowsFromDocument,
} from './proxyRouteUtils';

const ProxyGrid = ({ document, onEdit, onRemove, disabled, liveUrlBase }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const rows = useMemo(() => proxyRowsFromDocument(document), [document]);

  useEffect(() => {
    setPage(0);
  }, [document]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => (
      (row.originalRoute || '').toLowerCase().includes(term)
      || (row.proxyRoute || '').toLowerCase().includes(term)
      || (row.prefix || '').toLowerCase().includes(term)
      || (row.target || '').toLowerCase().includes(term)
      || String(row.appendPhpRoute || '').toLowerCase().includes(term)
    ));
  }, [rows, search]);

  const start = page * BROWSE_PAGE_SIZE;
  const pageRows = filtered.slice(start, start + BROWSE_PAGE_SIZE);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(0);
  };

  if (!document) {
    return null;
  }

  return (
    <div className="runtime-config-grid-section" data-testid="proxy-grid">
      <RuntimeConfigGridToolbar
        label="Filter proxy entries"
        placeholder="Search original route, proxy route, prefix, target, reroute…"
        value={search}
        onChange={handleSearchChange}
        testId="proxy-search"
      />

      <table className="runtime-config-table runtime-config-table-proxy">
        <thead>
          <tr>
            <th scope="col"><strong>Original Route</strong></th>
            <th scope="col"><strong>Proxy Route</strong></th>
            <th scope="col"><strong>Target Server</strong></th>
            <th scope="col"><strong>Reroute</strong></th>
            <th scope="col"><strong>Launch</strong></th>
            <th scope="col"><strong>Actions</strong></th>
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 ? (
            <tr>
              <td colSpan="6" className="runtime-config-empty">No proxy entries found.</td>
            </tr>
          ) : (
            pageRows.map((row) => {
              const launchUrl = buildLiveUrl(row.proxyRoute || row.prefix, { liveUrlBase });
              return (
                <tr key={row.prefix}>
                  <td className="runtime-config-mono">{row.originalRoute}</td>
                  <td className="runtime-config-mono">{row.proxyRoute}</td>
                  <td>{row.target}</td>
                  <td>{formatRerouteDisplay(row.appendPhpRoute)}</td>
                  <td>
                    {launchUrl ? (
                      <a
                        href={launchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="runtime-config-launch-link"
                        title={launchUrl}
                      >
                        Open
                      </a>
                    ) : (
                      '\u2014'
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="runtime-config-row-btn"
                      disabled={disabled}
                      aria-label={`Edit proxy mapping ${row.prefix}`}
                      onClick={() => onEdit(buildProxyEditPrefill(row))}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="runtime-config-row-btn runtime-config-row-btn-danger"
                      disabled={disabled}
                      aria-label={`Remove proxy mapping ${row.prefix}`}
                      onClick={() => onRemove({ prefix: row.prefix })}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <RuntimeConfigGridPagination
        count={filtered.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
      />
    </div>
  );
};

export default ProxyGrid;
